import { IconArrowCollapse } from '@assets/icon-arrow-collapse';
import { IconPlayEntity } from '@assets/icon-play-entity';
import { EntityInfo } from '@components/atoms/EntityInfo/EntityInfo';
import { FallbackCard } from '@components/atoms/FallbackCard/FallbackCard';
import { Link } from '@components/atoms/Link/Link';
import type {
  Episode as EpisodeScheme,
  FairuAsset,
  Provider,
  WatchLink as WatchLinkScheme,
} from '@lib/graphql/generated';
import { useFavoritesStore } from '@src/stores/favorites';
import { getUniqueEntries } from '@utils/common';
import { Image } from '@vgn-medien-holding/vgn-fe-components';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { WatchLink } from '../Watchlink/Watchlink';

export interface EpisodeProps {
  episode: Partial<
    {
      asset: Omit<FairuAsset, 'movies' | 'persons' | 'shows'>;
      // Queries select only a few Provider fields for providerEntry, never the whole entity.
      links: Array<(Omit<WatchLinkScheme, 'providerEntry'> & { providerEntry?: Partial<Provider> | null }) | null>;
    } & Omit<EpisodeScheme, 'asset' | 'links'>
  >;
}

export const Episode = ({ episode }: EpisodeProps) => {
  const { getTypeFavorites } = useFavoritesStore(
    useShallow((state) => ({
      getTypeFavorites: state.getTypeFavorites,
      favorites: state.favorites,
    })),
  );
  const [watchlinksOpen, setWatchlinksOpen] = useState(false);
  if (!episode) return;
  const uniqueLinks = getUniqueEntries(episode.links, ['providerEntry', 'id'])
    .sort((l) =>
      getTypeFavorites('Provider')
        ?.map((e) => e.entity?.id)
        ?.includes(l?.providerEntry?.id)
        ? -1
        : 1,
    )
    .sort((l) => (l?.providerEntry?.sponsoring ? -1 : 1));

  return (
    <div className="w-full">
      <div className="flex w-full border-gray-650/25 border-y bg-gray-820/75 lg:items-center lg:border-none lg:bg-transparent">
        <div className="p-4 font-herokid text-6xl text-gray-600 md:p-8 lg:min-w-[2em]">{episode?.nr}</div>
        <div className="grid w-full items-center gap-10 p-4 md:p-8 lg:grid-cols-[auto_3fr_1fr_auto_auto] lg:border-gray-650/25 lg:border-y lg:bg-gray-820/75">
          <div className="relative hidden aspect-video w-40 overflow-hidden rounded-lg lg:block">
            {episode?.asset?.url ? (
              <Image
                src={episode.asset.url}
                alt={episode?.title}
                fill
                sizes="(min-width: 992px) 320px, 0px"
                copyright=""
              />
            ) : (
              <FallbackCard />
            )}
          </div>
          <div>
            <div className="font-bold text-gray-500 text-sm uppercase tracking-2px">Episode {episode.nr}</div>
            <div className="hyphenate line-clamp-3 min-w-0 font-herokid text-white text-xl">{episode.title}</div>
          </div>
          {episode.summary && (
            <div
              className="static lg:pointer-events-none lg:absolute lg:hidden"
              dangerouslySetInnerHTML={{ __html: episode.summary }}
            ></div>
          )}
          <div className="grid grid-cols-[auto_1fr] items-center gap-8">
            <EntityInfo
              label="Spieldauer"
              value={episode.runtime}
              unit="min"
              classProps={{ root: 'mr-auto', label: 'text-gray-500' }}
            />
            <div className="justify-center text-center font-bold text-sm tracking-2px">{episode.year}</div>
          </div>
          <div className="group relative hidden w-12 justify-end justify-self-end lg:grid">
            {episode.links?.length > 0 && (
              <Link href={episode.links[0]?.link} title={episode.links[0]?.provider}>
                <IconPlayEntity
                  classProps={{
                    root: 'border-gray-650/25 group-hover:border-white/75 transition-colors duration-1000 rounded-full',
                    svg: 'w-12 h-12',
                  }}
                />
              </Link>
            )}
            <div className="pointer-events-none absolute right-0 bottom-full -mb-14 pb-16 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100">
              <div className="grid gap-2 rounded-xl border border-gray-650/25 bg-gray-820/80 p-6 backdrop-blur-sm">
                {uniqueLinks.map((link) => (
                  <WatchLink key={link.providerEntry?.id + link.price + link.pricing} link={link} color="white" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-gray-650/25 border-b bg-gray-820/75 p-6 text-center font-bold text-sm uppercase tracking-2px lg:hidden">
        <div
          className="flex cursor-pointer select-none items-center justify-center gap-4"
          role="button"
          tabIndex={0}
          onClick={() => {
            setWatchlinksOpen((prev) => !prev);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setWatchlinksOpen((prev) => !prev);
          }}
        >
          <IconArrowCollapse open={watchlinksOpen} />
          <div className="px-6 font-bold text-sm uppercase tracking-2px">Wo läuft&apos;s?</div>
          <IconArrowCollapse open={watchlinksOpen} />
        </div>
        {watchlinksOpen && (
          <div className="mx-auto flex w-full max-w-md flex-col justify-center gap-2 pt-6">
            {episode.links
              .sort((l) => (l?.providerEntry?.sponsoring ? -1 : 1))
              .map((link) => (
                <WatchLink key={link.id} link={link} color="white" />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
